import { getClientVersion } from "./createEvoClient";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SdkFunction = (options: any) => any;

/**
 * Wraps a v5 and v6 SDK function into a single function typed as the v5 variant.
 * At runtime, dispatches to v5 or v6 based on the client's tagged version.
 *
 * Works for same-name functions and renamed functions alike.
 * If a future schema regen causes v6's request/response types to diverge,
 * TypeScript will not catch it here (v6Fn is loosely typed).
 * Use `createAdaptedFunction` instead for those cases.
 */
export const createVersionedFunction = <V5Fn extends SdkFunction>(v5Fn: V5Fn, v6Fn: SdkFunction): V5Fn =>
  ((opts: { client?: object }) => {
    const version = opts?.client ? getClientVersion(opts.client) : "v5";
    return version === "v6" ? v6Fn(opts) : v5Fn(opts);
  }) as V5Fn;

/**
 * For functions where v6 request or response shapes have diverged from v5.
 * Adapts v6 inputs/outputs to match the v5 canonical type so consumers never see the difference.
 *
 * Use this when `createVersionedFunction(v5Fn, v6Fn)` would cause runtime shape mismatches
 * (e.g. after a schema regen where v6 added/removed/renamed fields).
 */
export const createAdaptedFunction = <V5Fn extends SdkFunction>({
  v5,
  v6,
  adaptV6Request,
  adaptV6Response,
}: {
  v5: V5Fn;
  v6: SdkFunction;
  adaptV6Request?: (v5Opts: Parameters<V5Fn>[0]) => unknown;
  adaptV6Response?: (v6Result: unknown) => unknown;
}): V5Fn =>
  (async (opts: { client?: object }) => {
    const version = opts?.client ? getClientVersion(opts.client) : "v5";
    if (version === "v6") {
      const adaptedOpts = adaptV6Request ? adaptV6Request(opts) : opts;
      const result = await v6(adaptedOpts);
      return adaptV6Response ? adaptV6Response(result) : result;
    }
    return v5(opts);
  }) as unknown as V5Fn;

/**
 * Wraps a v6-only SDK function. Rejects with a descriptive error when called on a v5 client.
 * Typed with v6 function signature since there is no v5 equivalent.
 */
export const createV6OnlyFunction = <V6Fn extends SdkFunction>(v6Fn: V6Fn, functionName: string): V6Fn =>
  ((opts: { client?: object }) => {
    const version = opts?.client ? getClientVersion(opts.client) : "v5";
    if (version !== "v6") {
      return Promise.reject(
        new Error(
          `Method "${functionName}" is not available in EVO v5. Ensure you are using an EVO v6 client (createEvoClient({ version: "v6" })).`
        )
      );
    }
    return v6Fn(opts);
  }) as V6Fn;
